import React, { useState } from "react";
import PageHeader from "../components/PageHeader";
import DataTable from "../components/DataTable";
import { useCallLogs, useLeads, useTeam } from "../../hooks/useFirebase";

const CallLogsPage = () => {
  const { callLogs, loading, addCallLog } = useCallLogs();
  const { leads } = useLeads();
  const { teamMembers } = useTeam();
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({
    leadId: "", leadName: "", phone: "", calledBy: "", calledByName: "",
    disposition: "", duration: "", notes: "", nextFollowUp: ""
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    if (name === "leadId") {
      const lead = leads.find((l) => l.id === value);
      if (lead) setFormData((prev) => ({ ...prev, leadId: value, leadName: lead.name, phone: lead.phone }));
    }
    if (name === "calledBy") {
      const m = teamMembers.find((x) => x.id === value);
      if (m) setFormData((prev) => ({ ...prev, calledBy: value, calledByName: m.name }));
    }
  };

  const handleSubmit = async () => {
    if (!formData.leadId || !formData.disposition) { alert("Lead & Disposition required!"); return; }
    try {
      await addCallLog(formData);
      setShowModal(false);
      setFormData({ leadId: "", leadName: "", phone: "", calledBy: "", calledByName: "", disposition: "", duration: "", notes: "", nextFollowUp: "" });
    } catch (e) { alert("Failed!"); }
  };

  const columns = [
    {
      header: "Lead",
      render: (row) => (
        <div>
          <div className="font-bold text-14 text-gray-900">{row.leadName}</div>
          <div className="text-12 text-gray-400">{row.phone}</div>
        </div>
      )
    },
    { header: "Called By", render: (row) => <span>{row.calledByName || row.calledBy}</span> },
    {
      header: "Disposition",
      render: (row) => (
        <span className={`status-badge badge-${row.disposition}`}>
          {row.disposition?.replace("_", " ")}
        </span>
      )
    },
    { header: "Duration", render: (row) => <span>⏱ {row.duration}</span> },
    {
      header: "Notes",
      render: (row) => (
        <span className="text-12 text-gray-600" title={row.notes}>
          {row.notes || "-"}
        </span>
      )
    }
  ];

  return (
    <div>
      <PageHeader title="Call Logs" breadcrumbs={["Admin", "Call Logs"]} />

      {/* Stats */}
      <div className="admin-grid admin-grid-5" style={{ marginTop: 24, marginBottom: 24 }}>
        {[
          { label: "Total Calls", value: callLogs.length, icon: "📞", cls: "qs-blue" },
          { label: "Answered", value: callLogs.filter((c) => c.disposition === "answered").length, icon: "✅", cls: "qs-green" },
          { label: "Busy", value: callLogs.filter((c) => c.disposition === "busy").length, icon: "⏰", cls: "qs-orange" },
          { label: "No Answer", value: callLogs.filter((c) => c.disposition === "no_answer").length, icon: "📵", cls: "qs-orange" },
          { label: "Callback", value: callLogs.filter((c) => c.disposition === "callback").length, icon: "🔄", cls: "qs-purple" },
        ].map((s, i) => (
          <div key={i} className={`quick-stat ${s.cls}`}>
            <div className="quick-stat-icon">{s.icon}</div>
            <div className="quick-stat-info">
              <div className="quick-stat-label">{s.label}</div>
              <div className="quick-stat-value">{s.value}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Toolbar */}
      <div className="admin-toolbar" style={{ marginBottom: 24 }}>
        <div className="toolbar-left">
          <h3 className="font-bold text-20 text-gray-900">📞 All Call Logs</h3>
        </div>
        <div className="toolbar-right">
          <button className="btn btn-primary" onClick={() => setShowModal(true)}>+ Log Call</button>
        </div>
      </div>

      <DataTable columns={columns} data={callLogs} actions={false} />

      {/* Log Call Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-box" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>📝 Log New Call</h3>
              <button className="modal-close-btn" onClick={() => setShowModal(false)}>✕</button>
            </div>
            <div className="modal-body">
              <div className="form-row">
                <div className="admin-form-group">
                  <label>Select Lead *</label>
                  <select name="leadId" value={formData.leadId} onChange={handleChange}>
                    <option value="">-- Select Lead --</option>
                    {leads.map((l) => <option key={l.id} value={l.id}>{l.name} - {l.phone}</option>)}
                  </select>
                </div>
                <div className="admin-form-group">
                  <label>Called By</label>
                  <select name="calledBy" value={formData.calledBy} onChange={handleChange}>
                    <option value="">-- Select Member --</option>
                    {teamMembers.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
                  </select>
                </div>
              </div>

              <div style={{ marginTop: 16 }}>
                <label className="text-14 font-semibold text-gray-700 mb-2" style={{ display: "block" }}>Disposition *</label>
                <div className="radio-grid cols-3">
                  {["answered", "busy", "no_answer", "wrong_number", "callback"].map((d) => (
                    <label key={d} className="radio-option">
                      <input type="radio" name="disposition" value={d} checked={formData.disposition === d} onChange={handleChange} />
                      <span>{d.replace("_", " ")}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="form-row" style={{ marginTop: 16 }}>
                <div className="admin-form-group">
                  <label>Duration</label>
                  <input type="text" name="duration" value={formData.duration} onChange={handleChange} placeholder="e.g. 4:32" />
                </div>
                <div className="admin-form-group">
                  <label>Next Follow-up Date</label>
                  <input type="date" name="nextFollowUp" value={formData.nextFollowUp} onChange={handleChange} />
                </div>
              </div>

              <div className="admin-form-group" style={{ marginTop: 16 }}>
                <label>Call Notes</label>
                <textarea name="notes" value={formData.notes} onChange={handleChange} placeholder="What was discussed?" rows="3" />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline" onClick={() => setShowModal(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={handleSubmit}>💾 Save to Firebase</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CallLogsPage;